import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Router from 'next/router';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { IRegion } from '@/types/explorer';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import {
  formatNumber,
  formatProgressData,
  getMultiplierFactor,
  getRoundPercent,
  getTotalKm,
} from '@/utils/explorer';
import AreaChart from '@/components/ui/charts/AreaChart';
import CustomTooltip from '@/components/ui/charts/CustomTooltip';
import { Button } from '@/components/shadcn/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/shadcn/dialog';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/shadcn/carousel/carousel';
import { externalUrls } from '@/constants/urls';
import { MapContextProvider } from '@/hooks/useMap';
import { P } from '@/components/shadcn/typography';
import DataBox from '../shared/DataBox';
import RegionMapStatic from './RegionMapStatic';

function RegionDetails({
  region,
  regions,
}: {
  region: IRegion;
  regions: IRegion[];
}) {
  const progress = getRoundPercent(region.overallProgress || 0);
  const totalKm = formatNumber(getTotalKm(region.totalCoveredL12Cells || 0));
  const uniqueKm = formatNumber(getTotalKm(region.uniqueCoveredL12Cells || 0));
  const contributors = formatNumber(region.totalUsers || 0);
  const multiplier = getMultiplierFactor(
    getRoundPercent(region.multiplierFactor || 0),
  );
  const weight = region.multiplier;
  const activityProgressData = formatProgressData(
    region.activityProgressByWeek || {},
    region.mostRecentDay || '',
  );
  const [screenWidth, setScreenWidth] = useState<number | undefined>();
  useEffect(() => {
    if (window) {
      setScreenWidth(window.innerWidth);
    }
  }, []);
  const coverageProgressData = formatProgressData(
    region.coverageProgressByWeek || {},
    region.mostRecentDay || '',
  );

  const rawData = region.resilienceProgressByWeek || {};
  const transformedRawData = Object.keys(rawData).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: rawData[curr].progress,
    }),
    {},
  );

  const resilienceProgressData = formatProgressData(
    transformedRawData,
    region.mostRecentDay || '',
  );

  const { roadLengthByTypeKm, images, description } = region.metadata;
  const totalRoadKm = (
    roadLengthByTypeKm.total ??
    Object.values(roadLengthByTypeKm).reduce((res, val) => res + val, 0)
  ).toLocaleString('en-US');

  const onMapPreviewClick = useCallback(() => {
    Router.push(`/network/regions/${region.name}/map`, undefined, {
      shallow: true,
    });
  }, [region.name]);

  const [imageIdx, setImageIdx] = useState(0);

  return (
    <>
      <div className="flex w-full flex-col gap-2 pb-8 md:max-w-2xl md:gap-4">
        {/* Imagery Section */}
        {images?.length > 0 ? (
          <>
            <Dialog>
              {/* Fullscreen view */}
              <Card className="hidden w-full flex-col gap-4 bg-neutral-900/40 p-8 md:flex">
                <CardHeader className="p-0">
                  <CardTitle className="text-xl font-normal">
                    Latest Views of {region.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid w-full grid-cols-4 grid-rows-2 gap-4 p-0">
                  {images.map(({ url, altText }, i) => (
                    <DialogTrigger
                      key={i}
                      className={i === 0 ? 'col-span-2 row-span-2' : ''}>
                      <img
                        className="aspect-[16/9] size-full rounded object-cover"
                        alt={altText}
                        src={url}
                        onClick={() => {
                          setImageIdx(i);
                        }}
                      />
                    </DialogTrigger>
                  ))}

                  <div className="flex aspect-[16/9] flex-col items-center justify-center gap-4 overflow-hidden rounded bg-neutral-900">
                    <P className="hidden max-w-[200px] text-center text-base md:text-xl lg:block">
                      Explore imagery from around the globe
                    </P>
                    <Link href={externalUrls.scout}>
                      <Button>Use Scout</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              {/* Modal content */}
              <DialogContent className="max-h-[90vh] w-screen max-w-[90vw] gap-0 overflow-hidden rounded-lg border-0 bg-neutral-1000 object-contain p-0">
                <DialogTitle className="invisible h-0">
                  Image Expansion Modal
                </DialogTitle>
                <Carousel className="size-full" opts={{ startIndex: imageIdx }}>
                  <CarouselContent className="ml-0 size-full">
                    {images.map(({ url, altText }, i) => (
                      <CarouselItem className="size-full pl-0" key={i}>
                        <img
                          className="aspect-[16/9]"
                          alt={altText}
                          src={url}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </DialogContent>
            </Dialog>
            {/* Mobile view */}
            <Card className="flex w-full flex-col gap-2 bg-neutral-900/40 p-3 md:hidden">
              <CardHeader className="p-0">
                <CardTitle className="text-sm leading-normal">
                  Latest views of {region.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex w-full flex-col gap-2 p-0">
                <img
                  className="w-full rounded object-cover"
                  alt={images[0]?.altText}
                  src={images[0]?.url}
                />
                <Link href={externalUrls.scout}>
                  <Button>Use Scout</Button>
                </Link>
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* Quick Facts Section */}
        <Card className="w-full flex-1 bg-neutral-900/40 p-3 md:min-w-24 md:p-8">
          <CardContent className="flex flex-col gap-2 p-0 md:gap-4">
            <CardTitle>
              <P className="text-sm font-normal md:text-xl">
                About {region.name}
              </P>
            </CardTitle>
            <CardDescription className="text-xs font-light text-neutral-300 md:text-base">
              {description}
            </CardDescription>
          </CardContent>
        </Card>

        <Card className="flex flex-col gap-3 bg-neutral-900/40 p-3 md:gap-8 md:p-8">
          {/* Bee Maps Data Section  */}
          <div className="flex items-center">
            <P className="text-sm md:text-xl">{`Network Data for ${region.name}`}</P>
          </div>
          <div className="flex w-full flex-col gap-3 md:flex-row md:gap-4">
            {[
              { title: 'Total Road KM', value: totalRoadKm },
              { title: 'Total Road KM Mapped', value: totalKm },
              { title: 'Unique Road KM Mapped', value: uniqueKm },
            ].map(({ title, value }) => (
              <DataBox
                key={title}
                title={title}
                titleClassName="text-base"
                valueClassName="text-[32px]"
                value={`${value} KM`}
                className="px-6 py-5"
              />
            ))}
          </div>

          {/* Map Preview Section */}
          <div
            className="size-full h-[400px] cursor-pointer overflow-hidden rounded md:h-[376px]"
            onClick={() => onMapPreviewClick()}>
            <MapContextProvider
              key="region-map-static"
              mapType="coverage"
              regionName={region.name}
              regions={regions}
              screenWidth={screenWidth}
              options={{
                enableStyles: false,
                enableLocation: false,
                enableSearch: false,
                rememberLocation: false,
                enableInteraction: false,
              }}>
              <RegionMapStatic region={region} regions={regions} />
            </MapContextProvider>
          </div>

          <div className="flex flex-col gap-2 md:gap-4">
            <div className="flex items-center">
              <P className="text-sm md:text-xl">{`Network Progress for ${region.name}`}</P>
            </div>
            {/* Charts */}
            <div className="flex w-full flex-col gap-2 md:flex-row md:gap-4">
              {[
                {
                  title: 'Activity Progress',
                  data: activityProgressData,
                  value: region.activityProgress,
                },
                {
                  title: 'Coverage Progress',
                  data: coverageProgressData,
                  value: region.coverageProgress,
                },
                {
                  title: 'Resilience Progress',
                  data: resilienceProgressData,
                  value: region.resilienceProgress?.progress,
                },
              ].map(({ title, data, value }, i) => (
                <Card
                  key={i}
                  className="flex h-[200px] w-full flex-col gap-2 bg-neutral-900 p-3 md:h-auto md:p-6">
                  <CardHeader className="p-0 text-xs font-thin md:text-sm">
                    <CardTitle className="font-thin leading-tight tracking-normal">
                      {title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="mb-1 text-base font-thin text-azure-400 md:text-3xl">
                      {getRoundPercent(value || 0)}%
                    </p>
                    <div className="h-32 w-full overflow-hidden md:h-56">
                      <AreaChart
                        data={data}
                        customTooltip={(props: any) => (
                          <CustomTooltip
                            line1={`${Math.round(
                              props?.payload[0]?.value || 0,
                            ).toLocaleString('en-US')}% ${title}`}
                            {...props}
                          />
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Number & Charts Section */}
            {/* Numbers */}
            <div className="flex w-full flex-col gap-3 md:flex-row md:gap-4">
              {[
                { title: 'Contributors', value: contributors },
                { title: 'Progress', value: `${progress}%` },
                { title: 'Multiplier', value: multiplier },
                { title: 'Weight', value: weight },
              ].map(({ title, value }) => (
                <DataBox
                  key={title}
                  title={title}
                  titleClassName="text-base"
                  valueClassName="text-[32px]"
                  value={value.toLocaleString('en-US')}
                  className="px-6 py-5"
                />
              ))}
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}

export default RegionDetails;
